import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { DEMO_GUEST, DemoPresence, demoBots as demoSwitch } from '../../collaboration/demoBots';
import { closeItemPanels } from '../../collaboration/rt/liveSession';
import { DemoShare } from '../../collaboration/share/demoShare';
import type { ShareState } from '../../collaboration/share/source';
import { store, type EditScope } from '../../state/store';

const noShare = () => () => {};

/**
 * The demo bots of a project of this browser (docs/collaboration.md «Demo bots»), their Share source, and «View as a
 * guest»: you become their guest `DEMO_GUEST` with the guest's rights while the bots stay. `state` is the demo's Share
 * state while it is the Share shown (no live room), else null.
 */
export function useDemoRoom({ on, shared, live, edit, projectId }: { on: boolean; shared: boolean; live: boolean; edit: EditScope; projectId: string }) {
  const [guestOf, setGuestOf] = useState<string | null>(null);
  const asGuest = guestOf === projectId;
  const ownProject = edit === 'all' || asGuest;
  const demo = useMemo(() => on && !shared && ownProject ? new DemoPresence({ store, people: true }) : null, [on, shared, ownProject]);
  useEffect(() => {
    if (!demo) return;
    demo.start(); demoSwitch.attach(demo);
    return () => { demo.stop(); if (demoSwitch.current() === demo) demoSwitch.attach(null); };
  }, [demo]);
  const guest = asGuest && !!demo;
  const demoShare = useMemo(() => new DemoShare(demo, guest ? 'guest' : 'owner'), [demo, guest]);
  const shown = demo && !live ? demoShare : null;
  const state: ShareState | null = useSyncExternalStore(shown?.subscribe ?? noShare, () => shown?.get() ?? null);
  const guestRole = guest ? state?.myRole ?? null : null;
  const viewAsGuest = useCallback((as: boolean) => {
    // Back as the owner, the edit is given back before the guest view ends, so the bots' room is never let go meanwhile.
    if (!as && store.get().edit !== 'all') store.set({ edit: 'all' });
    setGuestOf(as ? store.get().projectId : null);
  }, []);
  useEffect(() => {
    if (!guestRole) return;
    closeItemPanels(store, { edit: guestRole === 'viewer' ? 'none' : 'all' });
    demo?.viewAs(DEMO_GUEST);
    return () => demo?.viewAs(null);
  }, [guestRole, demo]);
  // The bots sent away while you are their guest: you are the owner again.
  useEffect(() => { if (asGuest && !demo) viewAsGuest(false); }, [asGuest, demo, viewAsGuest]);
  useEffect(() => { setGuestOf(current => current === projectId ? current : null); }, [projectId]);
  const canViewAsGuest = !!state && (guest || state.people.some(person => person.id === DEMO_GUEST));
  return { demo, demoShare, ownProject, guest, state, viewAsGuest, canViewAsGuest };
}
